import { formatUserName } from "@impero-com/common/utils/formatting";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { TableToolbar, TextTool } from "@impero-com/ui";
import { useOutletContext } from "react-router-dom";

import { AssignmentTable } from "org/user/components/AssignmentTable";
import { RemoveUserModal } from "org/user/components/RemoveUserModal";
import { ReplaceUserModal } from "org/user/components/ReplaceUserModal";
import { useSavedReportsForUserQuery } from "org/user/queries/useSavedReportsForUserQuery";
import { OutletContext } from "org/user/UserViewPage";
import { getFilterModeConfig } from "utils/table/getFilterModeConfig";

import { useRemoveSavedReportAccessRightUtils } from "./useRemoveSavedReportAccessRightUtils";
import { useReplaceSavedReportUserUtils } from "./useReplaceSavedReportUserUtils";
import {
    SavedReportRow,
    useUserSavedReportsListTable,
} from "./useUserSavedReportsListTable";

export function UserSavedReportsList() {
    const { user } = useOutletContext<OutletContext>();
    const userId = user.id;
    const { data } = useSavedReportsForUserQuery(userId);
    const { t } = useTranslation();
    const { table, tableId, getFilterComponent } =
        useUserSavedReportsListTable(data);
    const selectedRows = table.getFilteredSelectedRowModel().rows;
    const deselectAll = () => table.resetRowSelection();

    const {
        removeAccessRightToolState,
        isRemoveAccessRightModalOpen,
        openRemoveAccessRightModal,
        closeRemoveAccessRightModal,
        removeAccessRight,
        isRemovingAccessRight,
    } = useRemoveSavedReportAccessRightUtils({
        userId,
        selectedRows,
        deselectAll,
    });

    const {
        replaceUserToolState,
        isReplaceUserModalOpen,
        openReplaceUserModal,
        closeReplaceUserModal,
        replaceUser,
        isReplacingUser,
    } = useReplaceSavedReportUserUtils({ userId, selectedRows, deselectAll });

    return (
        <div>
            <RemoveUserModal
                onClose={closeRemoveAccessRightModal}
                onSubmit={() => void removeAccessRight()}
                open={isRemoveAccessRightModalOpen}
                loading={isRemovingAccessRight}
                message={t("AccessAndAssignment_RemoveSavedReportDialog_Text", {
                    user: formatUserName(user),
                    count: selectedRows.length,
                })}
            />
            <ReplaceUserModal
                open={isReplaceUserModalOpen}
                replaceType="WithUser"
                loading={isReplacingUser}
                onClose={closeReplaceUserModal}
                onClick={(userOrGroup) => void replaceUser(userOrGroup)}
                user={user}
            />
            <AssignmentTable<SavedReportRow>
                table={table}
                tableId={tableId}
                getFilterComponent={getFilterComponent}
                modeConfig={getFilterModeConfig(t)}
                toolbar={
                    <TableToolbar>
                        <TextTool
                            state={removeAccessRightToolState}
                            onClick={openRemoveAccessRightModal}
                        >
                            {t("AccessAndAssignment_RemoveDialog_Title")}
                        </TextTool>
                        <TextTool
                            state={replaceUserToolState}
                            onClick={openReplaceUserModal}
                        >
                            {t("AccessAndAssignment_Tool_ReplaceUser")}
                        </TextTool>
                    </TableToolbar>
                }
            />
        </div>
    );
}
